import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { IMAGES } from "@/data/site";
import { track } from "@/lib/api";

const PANELS = [
  {
    id: "build",
    label: "Build / Renovate",
    value: "Custom homes, renovations & commercial build-outs across Calgary & area.",
    to: "/services",
    image: IMAGES.buildPanel,
    index: "01",
  },
  {
    id: "grow",
    label: "Grow",
    value: "Branding, web, marketing & AI that grow the business you just built.",
    to: "/digital",
    image: IMAGES.growPanel,
    index: "02",
  },
  {
    id: "both",
    label: "Both",
    value: "One partner for the structure and the signal — built and launched together.",
    to: "/contact",
    image: IMAGES.portfolio1,
    index: "03",
  },
];

export function ChoosePathHub() {
  const [active, setActive] = useState(null);

  return (
    <section className="border-y border-border" data-testid="choose-path-hub" aria-label="Choose your path">
      <div className="flex flex-col md:flex-row min-h-[78vh]">
        {PANELS.map((panel, i) => {
          const isActive = active === panel.id;
          const dimmed = active && !isActive;
          return (
            <motion.div
              key={panel.id}
              onMouseEnter={() => setActive(panel.id)}
              onMouseLeave={() => setActive(null)}
              animate={{ flex: isActive ? 1.6 : 1 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className={`relative overflow-hidden group border-b md:border-b-0 md:border-r last:border-0 border-border min-h-[42vh] md:min-h-0`}
            >
              <Link
                to={panel.to}
                onClick={() => track("path_select", { path: panel.id })}
                data-testid={`hub-${panel.id}-panel`}
                className="absolute inset-0 flex flex-col justify-between p-7 sm:p-9 lg:p-12"
              >
                {/* Background image */}
                <img
                  src={panel.image}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                    isActive ? "opacity-40 scale-105 grayscale-0" : "opacity-20 grayscale"
                  } ${dimmed ? "opacity-10" : ""}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
                <div className="absolute inset-0 blueprint-grid opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
                {/* Accent top line */}
                <span
                  className={`absolute top-0 left-0 h-[3px] bg-primary transition-all duration-500 ${
                    isActive ? "w-full" : "w-0"
                  }`}
                />

                <div className="relative flex items-start justify-between">
                  <span className="font-mono-tech text-xs tracking-widest uppercase text-primary">
                    {panel.index} / Path
                  </span>
                  <ArrowUpRight
                    size={26}
                    className={`transition-all duration-500 ${
                      isActive ? "text-primary translate-x-1 -translate-y-1" : "text-muted-foreground"
                    }`}
                  />
                </div>

                <div className="relative">
                  <h3 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
                    {panel.label}
                  </h3>
                  <p
                    className={`mt-4 max-w-md text-muted-foreground leading-relaxed transition-all duration-500 ${
                      isActive ? "opacity-100 translate-y-0" : "opacity-70 md:opacity-0 md:translate-y-2"
                    }`}
                  >
                    {panel.value}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-foreground group-hover:text-primary transition-colors">
                    Explore path <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
